import React from 'react'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { IMAGE_DOMAIN, STATUS_KEYWORDS } from '../config'
import type { SkillKey } from '../types'

interface MarkdownRendererProps {
  content: string
  skill: SkillKey
  isStreaming?: boolean
}

/**
 * 加载状态组件
 */
const StatusLoader: React.FC<{ text: string }> = ({ text }) => (
  <span className="status-loader">
    <span className="status-spinner"></span>
    <span className="status-text">{text}</span>
  </span>
)

/**
 * 提取状态信息
 * 返回：{ cleanContent: 移除状态后的内容, activeStatus: 当前活动的状态文字（如果有） }
 */
function extractStatusInfo(content: string, isStreaming: boolean): {
  cleanContent: string
  activeStatus: string | null
} {
  if (!content) return { cleanContent: content, activeStatus: null }

  // 找到所有状态文字的位置
  const statusPositions: Array<{ index: number; keyword: string }> = []

  for (const keyword of STATUS_KEYWORDS) {
    let searchPos = 0
    while (true) {
      const idx = content.indexOf(keyword, searchPos)
      if (idx === -1) break
      statusPositions.push({ index: idx, keyword })
      searchPos = idx + keyword.length
    }
  }

  if (statusPositions.length === 0) {
    return { cleanContent: content, activeStatus: null }
  }

  // 按位置排序，找到最后一个
  statusPositions.sort((a, b) => a.index - b.index)
  const lastStatus = statusPositions[statusPositions.length - 1]

  // 检查最后一个状态后面是否有非状态内容
  const afterLastStatus = content.slice(lastStatus.index + lastStatus.keyword.length)
  const hasRealContent = afterLastStatus.trim().length > 0

  // 构建清理后的内容：移除所有状态文字
  let cleanContent = content
  for (const pos of statusPositions) {
    cleanContent = cleanContent.replace(pos.keyword, '')
  }

  // 流式输出且最后一个状态后面没有实际内容，显示加载动画
  const activeStatus = (isStreaming && !hasRealContent) ? lastStatus.keyword : null

  return { cleanContent, activeStatus }
}

/**
 * 预处理单行表格
 * 将单行内连续的 | 分隔符转换为标准的多行 Markdown 表格格式
 */
function preprocessSingleLineTable(content: string): string {
  if (!content) return content

  const lines = content.split('\n')

  const processedLines = lines.map((line) => {
    const trimmed = line.trim()
    if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) return line

    const pipeCount = (trimmed.match(/\|/g) || []).length
    if (pipeCount < 3) return line

    const cells = trimmed
      .slice(1, -1)
      .split('|')
      .map((cell) => cell.trim())

    if (cells.length < 2) return line

    // 启发式：单元格内不应该有过多冲突的 Markdown 字符
    const hasComplexMarkdown = cells.some(
      (cell) => /[<>\[\]]/.test(cell) && !/^[\u4e00-\u9fa5\w\s\-—:：、，。！？,.!?]+$/.test(cell)
    )
    if (hasComplexMarkdown) return line

    const header = `| ${cells.join(' | ')} |`
    const separator = `| ${cells.map(() => '---').join(' | ')} |`

    return `${header}\n${separator}`
  })

  return processedLines.join('\n')
}

/**
 * 预处理裸 URL，将纯 URL 转换为 Markdown 链接格式
 */
function preprocessBareUrls(content: string): string {
  if (!content) return content

  const protectedItems: string[] = []

  // 保护 Markdown 图片
  let result = content.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match) => {
    protectedItems.push(match)
    return `\x00PROT${protectedItems.length - 1}\x00`
  })

  // 保护 Markdown 链接
  result = result.replace(/\[([^\]]*)\]\(([^)]+)\)/g, (match) => {
    protectedItems.push(match)
    return `\x00PROT${protectedItems.length - 1}\x00`
  })

  // 保护 HTML 链接
  result = result.replace(/<a\s+[^>]*>[^<]*<\/a>/gi, (match) => {
    protectedItems.push(match)
    return `\x00PROT${protectedItems.length - 1}\x00`
  })

  // 保护其他 HTML 标签（包括 table/tr/td 等）
  result = result.replace(/<\/?[a-zA-Z][^>]*>/g, (match) => {
    protectedItems.push(match)
    return `\x00PROT${protectedItems.length - 1}\x00`
  })

  // 转换裸 URL
  const urlPattern = /(https?:\/\/[a-zA-Z0-9.\-_/?:=&%#~+]+)/g
  result = result.replace(urlPattern, (url) => `[${url}](${url})`)

  // 恢复保护的内容
  protectedItems.forEach((item, index) => {
    result = result.replace(`\x00PROT${index}\x00`, item)
  })

  return result
}

/**
 * 解析 Markdown 为 HTML（支持 HTML 标签和 GFM 表格）
 */
function renderMarkdownToHtml(content: string, skill: SkillKey): string {
  if (!content) return ''

  // 仅 learning 技能处理状态文字
  const { cleanContent } = skill === 'learning'
    ? extractStatusInfo(content, false)
    : { cleanContent: content }

  // 预处理：单行表格（仅 growth 技能）
  let processed = skill === 'growth' ? preprocessSingleLineTable(cleanContent) : cleanContent

  // 预处理裸 URL
  processed = preprocessBareUrls(processed)

  // 处理图片链接 - 添加域名前缀
  if (skill === 'activity' || skill === 'system') {
    processed = processed.replace(
      /!\[([^\]]*)\]\((\/files\/[^)]+)\)/g,
      (_match, alt, path) => `![${alt}](${IMAGE_DOMAIN}${path})`
    )
  }

  // 使用 marked 解析（GFM 模式自动支持 HTML 表格和 Markdown 表格）
  let html = marked.parse(processed, {
    gfm: true,
    breaks: true
  }) as string

  // 为所有链接添加 target="_blank" rel="noopener noreferrer"
  html = html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ')

  return html
}

/**
 * React Markdown 渲染器
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  skill,
  isStreaming = false
}) => {
  // 仅 learning 技能处理状态文字（用于动画展示）
  const shouldExtractStatus = skill === 'learning'
  const { activeStatus } = shouldExtractStatus
    ? extractStatusInfo(content, isStreaming)
    : { activeStatus: null }

  // 渲染 HTML 内容
  const rawHtml = React.useMemo(() => renderMarkdownToHtml(content, skill), [content, skill])

  // 使用 DOMPurify 清理 HTML，防止 XSS
  const cleanHtml = React.useMemo(() => {
    if (typeof window === 'undefined') return rawHtml
    return DOMPurify.sanitize(rawHtml, {
      USE_PROFILES: { html: true },
      // 允许表格相关属性
      ADD_ATTR: ['target', 'rel'],
    })
  }, [rawHtml])

  // 仅 learning 技能：替换链接文字为"作品链接"
  const finalHtml = React.useMemo(() => {
    if (skill !== 'learning') return cleanHtml
    return cleanHtml.replace(/<a([^>]*)>([^<]*)<\/a>/gi, (_match, attrs) => {
      return `<a${attrs}>作品链接</a>`
    })
  }, [cleanHtml, skill])

  return (
    <>
      {/* 加载状态动画 */}
      {activeStatus && <StatusLoader text={activeStatus} />}

      {/* 渲染内容（支持 Markdown 和 HTML 表格） */}
      <div dangerouslySetInnerHTML={{ __html: finalHtml }} />
    </>
  )
}
